import { useEffect, useState } from 'react';
import ActionButton from '../../../atoms/ActionButton';
import TextEdit from '../../../molecules/TextEdit';
import MaskEdit from '../../../organisms/MaskEdit';
import type { FinancialRepresentative } from '../../../../services/personApi';
import type { ErrorField } from '../../../../types/ErrorField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import {
    BasicFinancialRepresentativeDraft,
    isDuplicateBasic,
    toNewBasicFinancialRepresentative,
    validateBasicRepresentative,
} from './financialRepresentatives';

const MOBILE_MASK = '(__) _____-____';
const MOBILE_MASK_REPLACEMENT = { _: /\d/ };
const DUPLICATE_MESSAGE = 'Representante já incluído com os mesmos nome, e-mail e celular.';
const FULL_NAME_INPUT_ID = 'basicFinancialRepresentativeFullName';

interface Props {
    initialName: string;
    items: FinancialRepresentative[];
    onAdd: (item: FinancialRepresentative) => void;
    onCancel: () => void;
}

let nextLocalKey = 0;
const newLocalKey = (): string => `basic-${Date.now()}-${nextLocalKey++}`;

/**
 * Basic registration of a Financial Representative without an individual (AF-4180): Nome, E-mail
 * and Celular, added to the list of the "Dados Cadastrais" tab (saved with the tab). Invalid fields
 * and a representative already in the list with the same data are refused here.
 */
const BasicFinancialRepresentativeForm = ({ initialName, items, onAdd, onCancel }: Props) => {
    const [draft, setDraft] = useState<BasicFinancialRepresentativeDraft>({ fullName: initialName, email: '', phone: '' });
    const [errors, setErrors] = useState<ErrorField[]>([]);
    const [duplicate, setDuplicate] = useState(false);

    useEffect(() => {
        document.getElementById(FULL_NAME_INPUT_ID)?.focus();
    }, []);

    const change = (field: keyof BasicFinancialRepresentativeDraft, value: string) => {
        setDraft((current) => ({ ...current, [field]: value }));
        setErrors((current) => current.filter((error) => error.fieldName !== field));
        setDuplicate(false);
    };

    const handleAdd = () => {
        const fieldErrors = validateBasicRepresentative(draft);
        setErrors(fieldErrors);
        if (fieldErrors.length > 0) return;
        if (isDuplicateBasic(items, draft)) {
            setDuplicate(true);
            return;
        }
        onAdd(toNewBasicFinancialRepresentative(draft, newLocalKey()));
    };

    return (
        <div className="mt-2 rounded border border-gray-200 p-3 dark:border-slate-700">
            <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">Representante com dados básicos</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <TextEdit
                    id={FULL_NAME_INPUT_ID}
                    label="Nome"
                    value={draft.fullName}
                    maxLength={150}
                    errorMessage={getErrorMessage(errors, 'fullName')}
                    onChange={(event) => change('fullName', event.target.value)}
                />
                <TextEdit
                    id="basicFinancialRepresentativeEmail"
                    label="E-mail"
                    type="email"
                    value={draft.email}
                    maxLength={255}
                    errorMessage={getErrorMessage(errors, 'email')}
                    onChange={(event) => change('email', event.target.value)}
                />
                <MaskEdit
                    id="basicFinancialRepresentativePhone"
                    label="Celular"
                    mask={MOBILE_MASK}
                    replacement={MOBILE_MASK_REPLACEMENT}
                    value={draft.phone}
                    errorMessage={getErrorMessage(errors, 'phone')}
                    onChange={(event) => change('phone', event.target.value)}
                />
            </div>
            {duplicate ? <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">{DUPLICATE_MESSAGE}</p> : null}
            <div className="mt-3 flex justify-end gap-2">
                <ActionButton variant="secondary" size="sm" onClick={onCancel}>Cancelar</ActionButton>
                <ActionButton size="sm" onClick={handleAdd}>Adicionar</ActionButton>
            </div>
        </div>
    );
};

export default BasicFinancialRepresentativeForm;
